export type AerialMode = 'ground'|'ascent'|'apex'|'descent'|'flight'|'dive'
export type AerialEvent = 'jump'|'air-jump'|'super-jump'|'apex'|'flight-enter'|'flight-exit'|'flight-boost'|'dive'|'land'|null
export type AerialIntent = { jump?:boolean; superJump?:boolean; flight?:boolean; boost?:boolean; dive?:boolean }
export type AerialBody = {
  y:number
  vy:number
  aerialMode:AerialMode
  jumpsUsed:number
  juggleCount:number
  flightFuel:number
  aerialEvent:AerialEvent
  aerialEventTime:number
  aerialSerial:number
}

export const AERIAL_LIMITS = {
  gravity: 22,
  jumpVelocity: 7.6,
  airJumpVelocity: 7.05,
  superJumpVelocity: 11.4,
  maxJumps: 2,
  flightEnterHeight: .45,
  flightFuelDrain: .14,
  flightBoostDrain: .34,
  groundFuelRegen: .22,
  maxRiseSpeed: 12,
  maxFallSpeed: -14,
} as const
export const createAerialBody = ():AerialBody => ({
  y:0,
  vy:0,
  aerialMode:'ground',
  jumpsUsed:0,
  juggleCount:0,
  flightFuel:1,
  aerialEvent:null,
  aerialEventTime:0,
  aerialSerial:0,
})

const clamp=(v:number,min:number,max:number)=>Math.max(min,Math.min(max,v))
const signal=(body:AerialBody,event:Exclude<AerialEvent,null>,life=.28):AerialBody=>({
  ...body,
  aerialEvent:event,
  aerialEventTime:life,
  aerialSerial:body.aerialSerial+1,
})

export const isAirborne=(body:AerialBody)=>body.y>.001||body.aerialMode!=='ground'

export function stepAerial(body:AerialBody,input:AerialIntent,dt:number,canAct=true):AerialBody{
  const safeDt=Number.isFinite(dt)?clamp(dt,0,1/20):0
  let next:AerialBody={...body,aerialEventTime:Math.max(0,body.aerialEventTime-safeDt)}
  if(next.aerialEventTime<=0)next.aerialEvent=null
  if(!canAct&&next.aerialMode==='flight')next=signal({...next,aerialMode:'descent'},'flight-exit',.2)
  const grounded=next.y<=.001
  if(grounded){
    next.y=0
    next.juggleCount=0
    next.flightFuel=clamp(next.flightFuel+AERIAL_LIMITS.groundFuelRegen*safeDt,0,1)
    if(next.aerialMode!=='ground'){next={...signal(next,'land',.3),aerialMode:'ground',jumpsUsed:0,vy:0}}
  }

  if(canAct&&input.superJump&&grounded){
    next={...signal(next,'super-jump',.42),vy:AERIAL_LIMITS.superJumpVelocity,aerialMode:'ascent',jumpsUsed:1}
  }else if(canAct&&input.jump){
    if(grounded){
      next={...signal(next,'jump',.28),vy:AERIAL_LIMITS.jumpVelocity,aerialMode:'ascent',jumpsUsed:1}
    }else if(next.aerialMode!=='flight'&&next.jumpsUsed<AERIAL_LIMITS.maxJumps){
      next={...signal(next,'air-jump',.32),vy:AERIAL_LIMITS.airJumpVelocity,aerialMode:'ascent',jumpsUsed:next.jumpsUsed+1}
    }
  }

  if(canAct&&input.flight&&next.y>=AERIAL_LIMITS.flightEnterHeight&&next.flightFuel>.08){
    if(next.aerialMode==='flight')next={...signal(next,'flight-exit',.22),aerialMode:'descent'}
    else next={...signal(next,'flight-enter',.34),aerialMode:'flight',vy:Math.max(next.vy,1.2)}
  }
  if(canAct&&input.dive&&next.y>.05&&next.aerialMode!=='ground'){
    next={...signal(next,'dive',.24),aerialMode:'dive',vy:Math.min(next.vy,-8.8)}
  }

  const wasRising=next.vy>0
  if(next.aerialMode==='flight'){
    const drain=(AERIAL_LIMITS.flightFuelDrain+(input.boost?AERIAL_LIMITS.flightBoostDrain:0))*safeDt
    next.flightFuel=clamp(next.flightFuel-drain,0,1)
    if(input.boost&&next.flightFuel>0){
      if(next.aerialEvent!=='flight-boost')next=signal(next,'flight-boost',.18)
      next.vy=clamp(next.vy+12*safeDt,-3.5,6.8)
    }else if(input.dive){
      next.vy=clamp(next.vy-10*safeDt,-7.5,4)
    }else{
      next.vy*=Math.pow(.08,safeDt)
    }
    if(next.flightFuel<=0)next={...signal(next,'flight-exit',.2),aerialMode:'descent'}
  }else{
    next.vy=clamp(next.vy-AERIAL_LIMITS.gravity*safeDt,AERIAL_LIMITS.maxFallSpeed,AERIAL_LIMITS.maxRiseSpeed)
  }

  next.y=Math.max(0,next.y+next.vy*safeDt)
  if(next.y<=0){
    const landed=!grounded
    next={...next,y:0,vy:0,aerialMode:'ground',jumpsUsed:0,juggleCount:0}
    if(landed)next=signal(next,'land',.3)
    return next
  }

  if(next.aerialMode!=='flight'&&next.aerialMode!=='dive'){
    if(wasRising&&next.vy<=0)next={...signal(next,'apex',.16),aerialMode:'apex'}
    else if(next.vy>0)next.aerialMode='ascent'
    else if(next.aerialMode==='apex'&&next.aerialEventTime>0)next.aerialMode='apex'
    else next.aerialMode='descent'
  }
  return next
}
